import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PerformanceChart, { ChartDataPoint } from '../../components/PerformanceChart';
import ChildSelectionModal from '../../components/ChildSelectionModal';
import ChildSwitcherButton from '../../components/ChildSwitcherButton';
import { useChild } from '../../contexts/ChildContext';
import { parentApi } from '../../services/api';

const F = { fontFamily: 'Inter' } as const;

// Ödev Özeti status colors — same palette as the student Performansım screen.
const HW_GREEN = '#43c95c';
const HW_YELLOW = '#f5c518';
const HW_RED = '#e53e3e';

type Period = 'Bu Hafta' | 'Bu Ay' | 'Tüm Zamanlar';
const periodToParam: Record<Period, string> = { 'Bu Hafta': 'week', 'Bu Ay': 'month', 'Tüm Zamanlar': 'all' };

interface SubjectOption { id: number | 'all'; name: string }
interface TestResult { topic: string; subject: string; correctPct: number }
interface HomeworkBreakdown {
  total: number;
  fullCompleted: number;
  partialCompleted: number;
  notCompleted: number;
  fullCompletedPct: number;
}

// Donut visualizing the Doğru / Yanlış split with D. Ort in the center.
// Mirrors the student Performansım donut so the parent sees the same view.
const AccuracyDonut: React.FC<{ correct: number; wrong: number; avg: number }> = ({ correct, wrong, avg }) => {
  const total = correct + wrong;
  const size = 150;
  const stroke = 18;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const correctFrac = total > 0 ? correct / total : 0;
  const dash = correctFrac * c;
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5599b" strokeWidth={stroke} />
          {total > 0 && (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke="#3973bf"
              strokeWidth={stroke}
              strokeDasharray={`${dash} ${c - dash}`}
              strokeLinecap={correctFrac > 0 && correctFrac < 1 ? 'round' : 'butt'}
            />
          )}
        </g>
        <text x={size / 2} y={size / 2 - 1} textAnchor="middle" fontFamily="Inter" fontSize="28" fontWeight="700" fill="#2b2b2b">%{avg}</text>
        <text x={size / 2} y={size / 2 + 19} textAnchor="middle" fontFamily="Inter" fontSize="12" fontWeight="500" fill="#888">D. Ort</text>
      </svg>
      <div className="flex gap-4 mt-2">
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: '#3973bf' }} />
          <span style={{ ...F, fontSize: 12, fontWeight: 600, color: '#2b2b2b' }}>Doğru {correct}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: '#e5599b' }} />
          <span style={{ ...F, fontSize: 12, fontWeight: 600, color: '#2b2b2b' }}>Yanlış {wrong}</span>
        </div>
      </div>
    </div>
  );
};

const TestCard: React.FC<{ test: TestResult; accentColor: string }> = ({ test, accentColor }) => (
  <div className="rounded-[14px] block w-full" style={{ background: accentColor, padding: 2, boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
    <div className="flex rounded-[12px] overflow-hidden">
      <div className="flex-1 p-2.5 bg-[#fafafa]">
        <p style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b' }}>{test.topic}</p>
        <p style={{ ...F, fontSize: 11, fontWeight: 500, color: '#2b2b2b', marginTop: 2 }}>{test.subject}</p>
      </div>
      <div className="w-[72px] flex flex-col items-center justify-center flex-shrink-0" style={{ background: accentColor }}>
        <span style={{ ...F, fontSize: 10, fontWeight: 600, color: '#ffffff' }}>Doğru</span>
        <span style={{ ...F, fontSize: 18, fontWeight: 600, color: '#ffffff' }}>%{test.correctPct}</span>
      </div>
    </div>
  </div>
);

/**
 * Parent Performance — mirrors the student "Performansım" tab (Ödev Özeti,
 * Başarı Puanı + Soru Sayısı charts, En İyi/En Zayıf 3 Konu, and the
 * Doğru/Yanlış Oranı donut) so the parent sees exactly what the student sees.
 * The only intentional omission is the Digikoç Yorumu (AI) block.
 *
 * Data comes from /parent/children/:id/performance, which delegates to the
 * student performance service (same payload: chartData, questionsPerDay,
 * best3, worst3, stats, availableSubjects, domainMin/Max). Child selection is
 * handled by ChildContext.
 */
const ParentPerformance: React.FC = () => {
  const { selectedChild, children: kids, loading: childLoading } = useChild();
  const [period, setPeriod] = useState<Period>('Bu Hafta');
  const [subject, setSubject] = useState<SubjectOption>({ id: 'all', name: 'Tüm Dersler' });
  const [subjectOptions, setSubjectOptions] = useState<SubjectOption[]>([{ id: 'all', name: 'Tüm Dersler' }]);
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);

  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [questionsPerDay, setQuestionsPerDay] = useState<ChartDataPoint[]>([]);
  const [chartDomain, setChartDomain] = useState<{ min?: number; max?: number }>({});
  const [bestTests, setBestTests] = useState<TestResult[]>([]);
  const [worstTests, setWorstTests] = useState<TestResult[]>([]);
  const [stats, setStats] = useState({ correct: 0, wrong: 0, avg: 0, testCount: 0 });
  // How much of the child's work the konu panels can describe. Without this
  // the page silently implies the rest never happened — see the caption below.
  const [attribution, setAttribution] = useState({ answered: 0, detailed: 0, attributed: 0 });
  const [rawAssignments, setRawAssignments] = useState<any[]>([]);
  const [perfLoading, setPerfLoading] = useState(false);

  const load = useCallback((childId: string, p: Period, subj: SubjectOption) => {
    setPerfLoading(true);
    parentApi.childPerformance(childId, periodToParam[p], subj.id === 'all' ? undefined : subj.id)
      .then((res) => {
        const data: any = res.data ?? {};
        const chart: ChartDataPoint[] = (data.chartData ?? []).map((pt: any) => ({
          label: pt.label ?? '',
          value: Number(pt.value) || 0,
          submitted_at: pt.submitted_at,
        }));
        setChartData(chart);
        setQuestionsPerDay((data.questionsPerDay ?? []).map((pt: any) => ({
          label: pt.label ?? '',
          value: Number(pt.value) || 0,
          submitted_at: pt.submitted_at,
        })));
        setChartDomain(data.domainMin && data.domainMax ? { min: data.domainMin, max: data.domainMax } : {});

        // Subjects the child has attempted — merged with "Tüm Dersler".
        if (Array.isArray(data.availableSubjects)) {
          setSubjectOptions([{ id: 'all', name: 'Tüm Dersler' }, ...data.availableSubjects.map((s: any) => ({ id: s.id, name: s.name }))]);
        }

        const mapResult = (r: any): TestResult => ({
          topic: r.title || r.detail || r.topic || 'Konu',
          subject: r.subject_name || r.subject || '',
          correctPct: typeof r.accuracy_pct === 'number' ? r.accuracy_pct
            : typeof r.value === 'number' ? Math.round(r.value) : 0,
        });
        setBestTests((data.best3 ?? []).map(mapResult));
        setWorstTests((data.worst3 ?? []).map(mapResult));

        const s = data.stats ?? {};
        setStats({
          correct: s.correct ?? 0,
          wrong: s.wrong ?? 0,
          avg: s.accuracy_pct ?? 0,
          testCount: s.attempts_count ?? (data.chartData?.length ?? 0),
        });
        const a = data.attribution ?? {};
        setAttribution({
          answered: a.answered ?? 0,
          detailed: a.detailed ?? 0,
          attributed: a.attributed ?? 0,
        });
      })
      .catch(console.error)
      .finally(() => setPerfLoading(false));
  }, []);

  useEffect(() => {
    if (selectedChild) load(selectedChild.id, period, subject);
  }, [selectedChild, period, subject, load]);

  // Child assignments feed the Ödev Özeti (period-agnostic, subject-filtered).
  useEffect(() => {
    if (!selectedChild) { setRawAssignments([]); return; }
    parentApi.childAssignments(selectedChild.id)
      .then((res) => setRawAssignments(res.data ?? []))
      .catch(() => setRawAssignments([]));
  }, [selectedChild]);

  // Ödev Özeti breakdown — same rules as the mobile parent screen. "Kısmen"
  // (partial) = a completed submission with blank answers
  // (attempt_stats.score_blank); the student's draft-based partial isn't
  // observable to a parent. Subject-filtered by the header dropdown.
  const homeworkBreakdown: HomeworkBreakdown = useMemo(() => {
    const filtered = subject.id === 'all'
      ? rawAssignments
      : rawAssignments.filter((a) => String(a.assignment?.subject?.id) === String(subject.id));
    const COMPLETED = new Set(['submitted', 'graded', 'returned']);
    const total = filtered.length;
    let fullCompleted = 0, partialCompleted = 0, notCompleted = 0;
    for (const a of filtered) {
      if (COMPLETED.has(a.status)) {
        if ((a.attempt_stats?.score_blank ?? 0) > 0) partialCompleted += 1; else fullCompleted += 1;
      } else if (a.status === 'pending') {
        notCompleted += 1;
      }
    }
    const done = fullCompleted + partialCompleted;
    return { total, fullCompleted, partialCompleted, notCompleted, fullCompletedPct: total > 0 ? Math.round((done / total) * 100) : 0 };
  }, [rawAssignments, subject]);

  if (childLoading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (kids.length === 0) {
    return <p className="text-center text-gray-400 py-16">Henüz öğrenci eklenmedi.</p>;
  }
  if (!selectedChild) {
    return <ChildSelectionModal context="Performans" />;
  }

  const displayChart = chartData.length > 0 ? chartData : Array.from({ length: 7 }, (_, i) => ({ label: `Gün ${i + 1}`, value: 0 }));
  const displayQuestions = questionsPerDay.length > 0 ? questionsPerDay : Array.from({ length: 7 }, (_, i) => ({ label: `Gün ${i + 1}`, value: 0 }));
  const soruYMax = Math.max(10, Math.ceil(Math.max(0, ...questionsPerDay.map((d) => d.value)) / 10) * 10);

  return (
    <div>
      {/* Header — child + switcher on the left, chart filters on the right. */}
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Performans</h1>
          <span style={{ ...F, fontSize: 16, fontWeight: 500, color: '#3973bf' }}>
            — {selectedChild.name} {selectedChild.surname}
            {selectedChild.className && ` · ${selectedChild.className}`}
          </span>
          <ChildSwitcherButton />
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="bg-[#fafafa] rounded-[12px] px-5 py-3 flex items-center gap-2 cursor-pointer" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }} onClick={() => setShowPeriodDropdown((v) => !v)}>
              <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#2b2b2b' }}>{period}</span>
              <svg width="12" height="8" viewBox="0 0 12 8" fill="#2b2b2b"><path d="M1 1l5 5 5-5" /></svg>
            </div>
            {showPeriodDropdown && (
              <div className="absolute top-full right-0 mt-1 bg-white rounded-[12px] shadow-lg border border-gray-200 z-20 overflow-hidden min-w-[150px]">
                {(['Bu Hafta', 'Bu Ay', 'Tüm Zamanlar'] as Period[]).map((p) => (
                  <button key={p} onClick={() => { setPeriod(p); setShowPeriodDropdown(false); }}
                    className={`block w-full text-left px-5 py-3 text-[14px] font-semibold hover:bg-gray-50 ${period === p ? 'text-[#3973bf]' : 'text-[#2b2b2b]'}`} style={F}>
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="relative">
            <div className="bg-[#fafafa] rounded-[12px] px-5 py-3 flex items-center gap-2 cursor-pointer" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }} onClick={() => setShowSubjectDropdown((v) => !v)}>
              <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#2b2b2b' }}>{subject.name}</span>
              <svg width="12" height="8" viewBox="0 0 12 8" fill="#2b2b2b"><path d="M1 1l5 5 5-5" /></svg>
            </div>
            {showSubjectDropdown && (
              <div className="absolute top-full right-0 mt-1 bg-white rounded-[12px] shadow-lg border border-gray-200 z-20 overflow-hidden min-w-[180px] max-h-[300px] overflow-y-auto">
                {subjectOptions.map((opt) => (
                  <button key={String(opt.id)} onClick={() => { setSubject(opt); setShowSubjectDropdown(false); }}
                    className={`block w-full text-left px-5 py-3 text-[14px] font-semibold hover:bg-gray-50 ${subject.id === opt.id ? 'text-[#3973bf]' : 'text-[#2b2b2b]'}`} style={F}>
                    {opt.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Ödev Özeti (all-time homework rollup, not filtered) */}
      <div className="bg-[#fafafa] rounded-[24px] p-5 mb-5" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
        <div className="flex items-center justify-between mb-3">
          <h2 style={{ ...F, fontSize: 18, fontWeight: 600, color: '#2b2b2b' }}>Ödev Özeti</h2>
          <span style={{ ...F, fontSize: 14, fontWeight: 600, color: '#3973bf' }}>Tamamlama %{homeworkBreakdown.fullCompletedPct}</span>
        </div>
        <div className="w-full h-[12px] bg-[#e5e5e5] rounded-[8px] mb-4 overflow-hidden flex">
          {homeworkBreakdown.total > 0 && (
            <>
              <div className="h-full" style={{ width: `${(homeworkBreakdown.fullCompleted / homeworkBreakdown.total) * 100}%`, background: HW_GREEN }} title={`Tamamen Tamamlandı: ${homeworkBreakdown.fullCompleted}`} />
              <div className="h-full" style={{ width: `${(homeworkBreakdown.partialCompleted / homeworkBreakdown.total) * 100}%`, background: HW_YELLOW }} title={`Kısmen Tamamlandı: ${homeworkBreakdown.partialCompleted}`} />
              <div className="h-full" style={{ width: `${(homeworkBreakdown.notCompleted / homeworkBreakdown.total) * 100}%`, background: HW_RED }} title={`Hiç Yapılmadı: ${homeworkBreakdown.notCompleted}`} />
            </>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-[12px] p-3 text-center border border-gray-100">
            <p style={{ ...F, fontSize: 12, color: '#888' }}>Toplam Ödev</p>
            <p style={{ ...F, fontSize: 22, fontWeight: 700, color: '#2b2b2b' }}>{homeworkBreakdown.total}</p>
          </div>
          <div className="bg-white rounded-[12px] p-3 text-center border-2" style={{ borderColor: HW_GREEN }}>
            <p style={{ ...F, fontSize: 12, color: '#3aa055' }}>Tamamen Tamamlandı</p>
            <p style={{ ...F, fontSize: 22, fontWeight: 700, color: HW_GREEN }}>{homeworkBreakdown.fullCompleted}</p>
          </div>
          <div className="bg-white rounded-[12px] p-3 text-center border-2 border-[#f5c518]">
            <p style={{ ...F, fontSize: 12, color: '#a97e00' }}>Kısmen Tamamlandı</p>
            <p style={{ ...F, fontSize: 22, fontWeight: 700, color: '#f5c518' }}>{homeworkBreakdown.partialCompleted}</p>
          </div>
          <div className="bg-white rounded-[12px] p-3 text-center border-2 border-[#e53e3e]">
            <p style={{ ...F, fontSize: 12, color: '#c53030' }}>Hiç Yapılmadı</p>
            <p style={{ ...F, fontSize: 22, fontWeight: 700, color: '#e53e3e' }}>{homeworkBreakdown.notCompleted}</p>
          </div>
        </div>
      </div>

      <div className="flex gap-5">
        {/* LEFT COLUMN */}
        <div className="flex-1 min-w-0">
          <div className="bg-[#fafafa] rounded-[24px] p-6 mb-5" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
            {perfLoading ? (
              <div className="flex items-center justify-center" style={{ height: 320 }}>
                <div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                <div>
                  <p style={{ ...F, fontSize: 15, fontWeight: 700, color: '#2b2b2b', marginBottom: 4, textAlign: 'center' }}>Başarı Puanı (0 - 100)</p>
                  <p style={{ ...F, fontSize: 11.5, color: '#8a8a99', marginBottom: 8, textAlign: 'center' }}>
                    Öğrencinin çözdüğü testlerin doğruluk oranından hesaplanan 0–100 arası başarı puanı; zaman içindeki gidişatını gösterir.
                  </p>
                  <div className="bg-[#fafafa] rounded-[12px]" style={{ height: 320, boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                    <PerformanceChart data={displayChart} color="#3973bf" id="parent-perf" height={320} strokeWidth={6} domainMin={chartDomain.min} domainMax={chartDomain.max} />
                  </div>
                </div>
                <div>
                  <p style={{ ...F, fontSize: 15, fontWeight: 700, color: '#2b2b2b', marginBottom: 4, textAlign: 'center' }}>Soru Sayısı</p>
                  <p style={{ ...F, fontSize: 11.5, color: '#8a8a99', marginBottom: 8, textAlign: 'center' }}>
                    Öğrencinin her gün çözdüğü soru adedi; çalışma düzenini ve yoğunluğunu gösterir.
                  </p>
                  <div className="bg-[#fafafa] rounded-[12px]" style={{ height: 320, boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                    <PerformanceChart data={displayQuestions} color="#7c5cff" id="parent-soru" height={320} strokeWidth={6} domainMin={chartDomain.min} domainMax={chartDomain.max} yMax={soruYMax} />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-5">
            <div className="flex-1">
              <h2 style={{ ...F, fontSize: 16, fontWeight: 600, color: '#2b2b2b', marginBottom: 2 }}>En İyi 3 Konu</h2>
              <p style={{ ...F, fontSize: 11.5, color: '#8a8a99', marginBottom: 8 }}>Öğrencinin doğruluk oranı en yüksek olan konuları.</p>
              {bestTests.length === 0 ? (
                <p className="text-gray-400 text-sm">Veri yok.</p>
              ) : (
                <div className="space-y-3">{bestTests.map((t, i) => <TestCard key={i} test={t} accentColor="#3973bf" />)}</div>
              )}
            </div>
            <div className="flex-1">
              <h2 style={{ ...F, fontSize: 16, fontWeight: 600, color: '#2b2b2b', marginBottom: 2 }}>En Zayıf 3 Konu</h2>
              <p style={{ ...F, fontSize: 11.5, color: '#8a8a99', marginBottom: 8 }}>Öğrencinin doğruluk oranı en düşük, tekrar etmesi gereken konuları.</p>
              {worstTests.length === 0 ? (
                <p className="text-gray-400 text-sm">Veri yok.</p>
              ) : (
                <div className="space-y-3">{worstTests.map((t, i) => <TestCard key={i} test={t} accentColor="#e5599b" />)}</div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN — Doğru/Yanlış/D.Ort + donut + Test Adedi */}
        <div className="w-[260px] xl:w-[300px] flex-shrink-0 bg-[#fafafa] rounded-[16px] overflow-hidden self-start" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          <div className="bg-[#e5599b] px-4 py-2 flex items-center justify-around" style={{ borderRadius: '16px 16px 0 0' }}>
            {[{ l: 'Doğru', v: String(stats.correct) }, { l: 'Yanlış', v: String(stats.wrong) }, { l: 'D. Ort', v: `%${stats.avg}` }].map((s) => (
              <div key={s.l} className="text-center">
                <p style={{ ...F, fontSize: 11, fontWeight: 500, color: '#ffffff' }}>{s.l}</p>
                <p style={{ ...F, fontSize: 18, fontWeight: 600, color: '#fafafa' }}>{s.v}</p>
              </div>
            ))}
          </div>
          <div className="p-4">
            <p style={{ ...F, fontSize: 13, fontWeight: 700, color: '#2b2b2b', marginBottom: 2 }}>Doğru / Yanlış Oranı</p>
            <p style={{ ...F, fontSize: 11.5, color: '#8a8a99', marginBottom: 6 }}>Öğrencinin çözdüğü tüm testlerdeki doğru ve yanlış cevaplarının dağılımı.</p>
            <div className="rounded-[12px] mb-3 flex items-center justify-center py-4" style={{ border: '1px solid #e5599b', boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
              <AccuracyDonut correct={stats.correct} wrong={stats.wrong} avg={stats.avg} />
            </div>
            <p style={{ ...F, fontSize: 13, fontWeight: 700, color: '#2b2b2b' }}>Test Adedi : {stats.testCount}</p>
            {/* Konu panels can only describe questions tagged to a subject_unit.
                Older submissions stored a score with no per-question detail, so
                that figure is well below the answered total. Saying so is the
                difference between "eksik veri" and a parent concluding the app
                lost their child's work. */}
            {attribution.answered > 0 && attribution.attributed < attribution.answered && (
              <p style={{ ...F, fontSize: 11, color: '#8a8a99', marginTop: 6, lineHeight: 1.45 }}>
                Konu bazlı analiz, çözülen {attribution.answered} sorunun{' '}
                {attribution.attributed} tanesini kapsıyor. Eski sürümlerle gönderilen
                testlerde soru bazlı kayıt tutulmadığı için bu testler konu
                dağılımında görünmez; yukarıdaki toplamlara dâhildir.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentPerformance;
